import type { PerfGpuTimer } from './perf/GpuTimer';
import type { AppRenderer } from '../render/Renderer';

export type PerfQualityState = 'full' | 'reduced';

/**
 * Tiny on-screen perf overlay. Auto-mounts in dev mode; press backtick to toggle.
 *
 * Shows: rolling-mean FPS over the last 60 frames, draw calls per frame,
 * triangles drawn per frame, and a coarse per-pass cost. Useful for
 * answering "is the canyon shader slow, or is it draw calls, or is it
 * something else?" without pulling in stats.js.
 */
export class PerfHud {
  private el: HTMLDivElement;
  private renderer: AppRenderer;
  private gpuTimer: PerfGpuTimer | null;
  private frames: number[] = [];
  private lastT = performance.now();
  /** Raw RAF cadence sample buffer — independent of render commits. Used to
   *  distinguish "browser is only giving us 60Hz RAF" from "we're skipping
   *  half our renders." */
  private rafFrames: number[] = [];
  private rafLastT = 0;
  /** Smallest inter-RAF interval ever observed. The killer signal for "is
   *  the browser capable of >60Hz on this machine?" — if this stays at ~16ms
   *  for the whole session, the browser cannot deliver more than 60Hz. */
  private minRafDtMs = Number.POSITIVE_INFINITY;
  /** Display-level RAF facts, set externally once at bootstrap. Show in HUD so
   *  the user can see at a glance: what does the OS think the monitor is doing,
   *  and what cadence does a bare RAF probe actually observe? */
  private rafDiagnosticsScreenHz: number | null = null;
  private rafDiagnosticsBareHz: number | null = null;
  private quality: PerfQualityState = 'full';
  private visible = true;
  private targetHz: number | null = null;
  /** Effective render scale applied by DrsSystem. In WebGPU scene-resolution
   *  mode the renderer's pixel ratio is locked to renderScaleMax (the cap),
   *  and the real resolution is driven by postFx.setRenderScale. Bootstrap
   *  calls setEffectiveScale on every applyRenderScale so this HUD reflects
   *  the actual sampled resolution rather than the renderer-internal cap. */
  private effectiveScale: number | null = null;

  constructor(_parent: HTMLElement, renderer: AppRenderer, gpuTimer: PerfGpuTimer | null = null) {
    this.renderer = renderer;
    this.gpuTimer = gpuTimer;
    const el = document.createElement('div');
    el.style.cssText = `
      position: fixed; top: 4px; left: 50%; transform: translateX(-50%);
      z-index: 2147483647;
      font: 12px/1.4 ui-monospace, Menlo, Consolas, monospace;
      background: rgba(0,0,0,0.74); color: #c8f7ff; padding: 6px 14px;
      border: 1px solid #2a4a55; border-radius: 4px;
      pointer-events: none; white-space: pre;
    `;
    el.textContent = 'perf';
    // Mount on body (outside the canvas parent's stacking context) so the
    // HUD always sits above the in-game UI overlays.
    document.body.appendChild(el);
    this.el = el;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.el.style.display = visible ? 'block' : 'none';
  }

  toggleVisible(): boolean {
    this.setVisible(!this.visible);
    return this.visible;
  }

  isVisible(): boolean {
    return this.visible;
  }

  setQualityState(state: PerfQualityState): void {
    this.quality = state;
  }

  setEffectiveScale(scale: number | null): void {
    this.effectiveScale = scale;
  }

  setTargetHz(hz: number): void {
    this.targetHz = hz;
  }

  /** Public accessor for the on-screen element. Useful for tests and for
   *  external positioning. */
  get element(): HTMLDivElement {
    return this.el;
  }

  /** One-shot diagnostic write: what the OS reports as the monitor refresh
   *  rate, and what a bare RAF probe actually measured. Either field can be
   *  null if not yet known/available. */
  setRafDiagnostics(info: { screenHz: number | null; bareRafHz: number | null }): void {
    this.rafDiagnosticsScreenHz = info.screenHz;
    this.rafDiagnosticsBareHz = info.bareRafHz;
  }

  /** Call once per RAF callback, before any render-cap gate. Samples the raw
   *  browser frame cadence so the HUD can show whether a low FPS reading is
   *  caused by the browser pacing RAF or by the engine skipping renders. */
  tickRaf(now: number): void {
    if (this.rafLastT > 0) {
      const dt = now - this.rafLastT;
      if (dt > 0 && dt < 1000) {
        this.rafFrames.push(dt);
        if (this.rafFrames.length > 60) this.rafFrames.shift();
        if (dt < this.minRafDtMs) this.minRafDtMs = dt;
      }
    }
    this.rafLastT = now;
  }

  tick(): void {
    const now = performance.now();
    const dt = now - this.lastT;
    this.lastT = now;
    this.frames.push(dt);
    if (this.frames.length > 60) this.frames.shift();
    if (!this.visible) return;
    const meanDt = this.frames.reduce((a, b) => a + b, 0) / this.frames.length;
    const fps = 1000 / meanDt;
    const info = this.renderer.info;
    const gpuFragment = this.gpuTimer
      ? this.gpuTimer.available()
        ? ` | GPU ${this.gpuTimer.totalMean().toFixed(1)}ms`
        : ' | GPU n/a'
      : '';
    const triK = info.render.triangles >= 1000
      ? `${(info.render.triangles / 1000).toFixed(0)}k`
      : `${info.render.triangles}`;
    const rendererDpr = this.renderer.getPixelRatio();
    const effective = this.effectiveScale ?? rendererDpr;
    const dprFragment = this.effectiveScale !== null && Math.abs(this.effectiveScale - rendererDpr) > 0.005
      ? ` | DPR ${effective.toFixed(2)} (cap ${rendererDpr.toFixed(2)})`
      : ` | DPR ${effective.toFixed(2)}`;
    const hzFragment = this.targetHz !== null ? ` @ ${Math.round(this.targetHz)}Hz` : '';
    const rafFragment = this.rafFrames.length >= 2
      ? ` rAF ${Math.round(1000 / (this.rafFrames.reduce((a, b) => a + b, 0) / this.rafFrames.length))}`
      : '';
    const minFragment = Number.isFinite(this.minRafDtMs)
      ? ` min ${this.minRafDtMs.toFixed(1)}ms`
      : '';
    const screenFragment = this.rafDiagnosticsScreenHz !== null
      ? ` screen ${Math.round(this.rafDiagnosticsScreenHz)}`
      : '';
    const bareFragment = this.rafDiagnosticsBareHz !== null
      ? ` bare ${Math.round(this.rafDiagnosticsBareHz)}`
      : '';
    const memFragment = readHeapMb();
    this.el.textContent =
      `FPS ${fps.toFixed(1)} (${meanDt.toFixed(1)}ms)${rafFragment}${minFragment}${screenFragment}${bareFragment}${hzFragment}${gpuFragment}` +
      dprFragment +
      ` | Draws ${info.render.calls} | Tris ${triK}` +
      ` | Programs ${rendererProgramCount(info)}${memFragment} | ${this.quality}`;
  }

  destroy(): void {
    this.el.remove();
  }
}

function rendererProgramCount(info: AppRenderer['info']): number {
  const webglPrograms = (info as { programs?: unknown[] }).programs;
  if (Array.isArray(webglPrograms)) return webglPrograms.length;
  return (info as { memory?: { programs?: number } }).memory?.programs ?? 0;
}

/** Chrome-only JS heap size in MB. Empty string elsewhere so the layout stays clean. */
function readHeapMb(): string {
  const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  if (!mem || typeof mem.usedJSHeapSize !== 'number') return '';
  return ` | Heap ${(mem.usedJSHeapSize / 1024 / 1024).toFixed(0)}MB`;
}
